import { api, baseUrl } from "./axios"
import Cookie from "js-cookie";
import { axiosApp } from "./axiosApp";
import { BASE_AUTH_URL, BASE_LOGIN_URL } from '../config'; // Adjust import paths as needed

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const redirectToLogin = () => {
  const currentUrl = window.location.href;
  const encodedRedirect = encodeURIComponent(currentUrl);
  window.location.href = `${BASE_LOGIN_URL}?redirectTo=${encodedRedirect}`;
};

const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    console.log("🔄 Token expired, attempting refresh...");
    const response = await fetch(`${BASE_AUTH_URL}/auth/refresh`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Refresh failed');
    }

    console.log("✅ Token refreshed successfully");
    processQueue(null, true);
    isRefreshing = false;
    return true;

  } catch (refreshError) {
    console.error("⛔ Token refresh failed:", refreshError);
    processQueue(refreshError, null);
    isRefreshing = false;

    redirectToLogin();
    throw refreshError;
  }
};

const makeAuthenticatedRequest = async (url, options, maxRetries = 1) => {
  const token = Cookie.get('token') || null;

  const requestOptions = {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    credentials: 'include'
  };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, requestOptions);

      // If we get a 401, try to refresh token
      if (response.status === 401 && attempt < maxRetries) {
        console.log(`🔄 Attempt ${attempt + 1}: Got 401, trying to refresh token...`);

        await refreshToken();

        // Update token for retry
        const newToken = Cookie.get('token') || null;
        if (newToken) {
          requestOptions.headers.Authorization = `Bearer ${newToken}`;
        }
        continue; // Retry the request
      }

      return response;

    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      console.log(`🔄 Attempt ${attempt + 1} failed, retrying...`);
    }
  }
};

export const sendMessage = async (body, onChunk, onComplete, onError) => {
  try {
    const response = await makeAuthenticatedRequest(`${baseUrl}/message/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `HTTP error! status: ${response.status}` };
      }
      // Throw in the same format as axios
      throw { response: { data: errorData } };
    }

    const reader = response.body?.getReader();
    // Check if ReadableStream is supported
    if (!reader) {
      throw new Error('ReadableStream not supported');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Keep the last incomplete line in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'chunk') {
              fullResponse += data.content;
              onChunk?.(data.content);
            } else if (data.type === 'done') {
              onComplete?.({ fullResponse: data.fullResponse || fullResponse, newConversation: data.newConversation || null, conversationTitle: data.conversationTitle || undefined });
              return {
                success: true,
                data: data.fullResponse || fullResponse,
                fullResponse: data.fullResponse || fullResponse
              };
            } else if (data.type === 'error') {
              onError?.(data.error || 'An error occurred');
              throw { response: { data: { message: data.error || 'Stream error' } } };
            }
          } catch (parseError) {
            console.warn('Failed to parse SSE data:', line, parseError);
          }
        }
      }
    }

    return { success: true, data: fullResponse, fullResponse };

  } catch (error) {
    console.error('SSE Error:', error);
    onError?.(error.message || (error.response?.data?.message) || 'Connection failed');

    // Maintain axios error format
    throw error;
  }
};

export const sendImageMessage = async (formData, onChunk, onComplete, onError) => {
  try {
    const response = await makeAuthenticatedRequest(`${baseUrl}/message/send-image`, {
      method: 'POST',
      headers: {
        'Accept': 'text/event-stream',
      },
      body: formData
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `HTTP error! status: ${response.status}` };
      }
      throw { response: { data: errorData } };
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('ReadableStream not supported');

    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'chunk') {
              fullResponse += data.content;
              onChunk?.(data.content);
            } else if (data.type === 'done') {
              onComplete?.({ fullResponse: data.fullResponse || fullResponse, newConversation: data.newConversation || null,conversationTitle:data.conversationTitle });
              return { success: true, data: data.fullResponse || fullResponse, fullResponse: data.fullResponse || fullResponse };
            } else if (data.type === 'error') {
              onError?.(data.error || 'An error occurred');
              throw { response: { data: { message: data.error || 'Stream error' } } };
            }
          } catch (parseError) {
            console.warn('Failed to parse SSE data:', line, parseError);
          }
        }
      }
    }

    return { success: true, data: fullResponse, fullResponse };

  } catch (error) {
    console.error('SSE Image Error:', error);
    onError?.(error.message || (error.response?.data?.message) || 'Connection failed');
    throw error;
  }
};
export const sendPdfMessage = async (formData, onChunk, onComplete, onError) => {
  try {
    const response = await makeAuthenticatedRequest(`${baseUrl}/message/send-pdf`, {
      method: 'POST',
      headers: {
        'Accept': 'text/event-stream',
      },
      body: formData
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `HTTP error! status: ${response.status}` };
      }
      throw { response: { data: errorData } };
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('ReadableStream not supported');

    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'chunk') {
              fullResponse += data.content;
              onChunk?.(data.content);
            } else if (data.type === 'done') {
              onComplete?.({ fullResponse: data.fullResponse || fullResponse, newConversation: data.newConversation || null,conversationTitle:data.conversationTitle });
              return { success: true, data: data.fullResponse || fullResponse, fullResponse: data.fullResponse || fullResponse };
            } else if (data.type === 'error') {
              onError?.(data.error || 'An error occurred');
              throw { response: { data: { message: data.error || 'Stream error' } } };
            }
          } catch (parseError) {
            console.warn('Failed to parse SSE data:', line, parseError);
          }
        }
      }
    }

    return { success: true, data: fullResponse, fullResponse };

  } catch (error) {
    console.error('SSE Image Error:', error);
    onError?.(error.message || (error.response?.data?.message) || 'Connection failed');
    throw error;
  }
};
export const fetchAllMessagesApi = async (conversationId) => {
  try {
    const response = await axiosApp.get(`/message/${conversationId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch messages:', error);
    throw error;
  }
};

export const addGreetingMessageApi = async (conversationId, greeting) => {
  try {
    const response = await axiosApp.post('/message/greeting', {
      conversationId,
      greeting
    });
    return response.data;
  } catch (error) {
    console.error('Failed to add greeting message:', error);
    throw error;
  }
};